import {
  AuthorizeModalTextViewState,
  AuthorizeModalTextAction,
  AuthorizeModalTextActionType,
  AuthorizeModalTextStep,
} from '../../../types';

/**
 * The starting state, all texts are hidden in this state.
 */
export const DEFAULT_STATE: AuthorizeModalTextViewState = {
  step: AuthorizeModalTextStep.Start,
  configurable: false,
  headerVisible: false,
  headerDividerVisible: false,
  headerTemplate: '',
  descriptionTemplate: '',
  legalTextTemplate: '',
  userNameKnown: false,
  templateVariables: {
    clientName: 'the store',
    store: 'the store',
  },
};

/**
 * The default reducer for authorization flows requiring a SMS verification, is also
 * used as the basis for email auth and classic customer accounts.
 *
 * @param {AuthorizeModalTextAction} action - The action to process
 * @param {AuthorizeModalTextViewState} state - The current state of the text view
 * @returns {AuthorizeModalTextViewState} - The updated state of the text view
 */
export function defaultPhoneAuthReducer(
  action: AuthorizeModalTextAction,
  state: AuthorizeModalTextViewState,
): AuthorizeModalTextViewState {
  switch (action.type) {
    case AuthorizeModalTextActionType.Init:
      return {
        ...state,
        configurable: true,
        headerVisible: true,
        headerDividerVisible: action.payload.userFound,
        step: action.payload.userFound
          ? AuthorizeModalTextStep.OneClick
          : AuthorizeModalTextStep.Start,
        userNameKnown: action.payload.userNameKnown || false,
        templateVariables: {
          ...state.templateVariables,
          ...action.payload,
          store: action.payload.clientName,
        },
        headerTemplate: 'login_with_shop.auth_modal.login_title',
        descriptionTemplate: 'login_with_shop.auth_modal.login_description',
        legalTextVariant: 'generic',
        legalTextTemplate: 'login_with_shop.auth_modal.login_footer',
      };
    case AuthorizeModalTextActionType.Restart:
      return {
        ...state,
        configurable: true,
        headerDividerVisible: false,
        step: AuthorizeModalTextStep.Start,
        userNameKnown: false,
        headerTemplate: 'login_with_shop.auth_modal.login_title',
        descriptionTemplate: 'login_with_shop.auth_modal.login_description',
        legalTextVariant: 'generic',
        legalTextTemplate: 'login_with_shop.auth_modal.login_footer',
      };
    case AuthorizeModalTextActionType.UserMatched:
      if (action.payload.userCookieExists) {
        return {
          ...state,
          configurable: true,
          headerDividerVisible: true,
          step: AuthorizeModalTextStep.OneClick,
          userNameKnown: action.payload.hasName || false,
        };
      }

      return {
        ...state,
        userNameKnown: action.payload.hasName || false,
      };
    case AuthorizeModalTextActionType.UserNotMatched:
      return {
        ...state,
        step: AuthorizeModalTextStep.SignUp,
        legalTextVariant: 'signup',
        headerDividerVisible: true,
        userNameKnown: false,
        configurable: false,
        headerTemplate: 'login_with_shop.auth_modal.signup_title',
        descriptionTemplate: 'login_with_shop.auth_modal.signup_description',
      };
    case AuthorizeModalTextActionType.VerificationStepChanged: {
      const {step, phone = '', email = ''} = action.payload;
      return {
        ...state,
        step:
          step === 'email'
            ? AuthorizeModalTextStep.EmailVerification
            : AuthorizeModalTextStep.PhoneVerification,
        configurable: false,
        headerDividerVisible: true,
        templateVariables: {
          ...state.templateVariables,
          phoneNumber: phone.replaceAll(' ', '\xa0'),
          email,
        },
        ...(step === 'email' && {
          headerTemplate: 'login_with_shop.auth_modal.login_email_title',
          descriptionTemplate:
            'login_with_shop.auth_modal.login_email_description',
          legalTextVariant: 'generic',
          legalTextTemplate: 'login_with_shop.auth_modal.login_email_footer',
        }),
        ...(step === 'sms' && {
          headerTemplate: 'login_with_shop.auth_modal.login_sms_title',
          descriptionTemplate:
            'login_with_shop.auth_modal.login_sms_description',
          legalTextVariant: 'generic',
          legalTextTemplate: 'login_with_shop.auth_modal.login_sms_footer',
        }),
      };
    }
  }

  return state;
}

/**
 * The default reducer for authorization flows requiring an email verification
 *
 * @param {AuthorizeModalTextAction} action - The action to process
 * @param {AuthorizeModalTextViewState} state - The current state of the text view
 * @returns {AuthorizeModalTextViewState} - The updated state of the text view
 */
export function defaultEmailAuthReducer(
  action: AuthorizeModalTextAction,
  state: AuthorizeModalTextViewState,
): AuthorizeModalTextViewState {
  const newState = defaultPhoneAuthReducer(action, state);
  const signUpHeaderTemplate = 'verified_email_auth.auth_modal.signup_title';

  // If the step hasn't changed, don't update the state, this is important for the email auth templates since
  // we hide the legal text when the user lands on a verification step after the signup step.
  if (
    state.step === newState.step &&
    [
      AuthorizeModalTextStep.EmailVerification,
      AuthorizeModalTextStep.PhoneVerification,
    ].includes(state.step)
  ) {
    return newState;
  }

  switch (newState.step) {
    case AuthorizeModalTextStep.Start:
      return {
        ...newState,
        headerTemplate: 'verified_email_auth.auth_modal.login_title',
        descriptionTemplate: '',
        legalTextVariant: 'signup',
      };
    case AuthorizeModalTextStep.OneClick:
      return {
        ...newState,
        legalTextVariant: 'generic',
        legalTextTemplate: 'login_with_shop.auth_modal.login_footer',
      };
    case AuthorizeModalTextStep.SignUp:
      return {
        ...newState,
        headerTemplate: signUpHeaderTemplate,
        descriptionTemplate:
          'verified_email_auth.auth_modal.signup_description',
        legalTextVariant: 'signup',
      };
    case AuthorizeModalTextStep.PhoneVerification:
      return {
        ...newState,
        headerTemplate: 'login_with_shop.auth_modal.login_sms_title',
        descriptionTemplate: 'login_with_shop.auth_modal.login_sms_description',
        legalTextVariant:
          state.headerTemplate === signUpHeaderTemplate ? undefined : 'generic',
        legalTextTemplate: 'login_with_shop.auth_modal.login_sms_footer',
      };
    case AuthorizeModalTextStep.EmailVerification:
      return {
        ...newState,
        headerTemplate: 'login_with_shop.auth_modal.login_email_title',
        descriptionTemplate:
          'login_with_shop.auth_modal.login_email_description',
        legalTextVariant:
          state.headerTemplate === signUpHeaderTemplate ? undefined : 'generic',
        legalTextTemplate: 'login_with_shop.auth_modal.login_email_footer',
      };
  }

  return newState;
}

/**
 * The reducer for sign up with Shop on classic customer accounts
 *
 * @param {AuthorizeModalTextAction} action - The action to process
 * @param {AuthorizeModalTextViewState} state - The current state of the text view
 * @returns {AuthorizeModalTextViewState} - The updated state of the text view
 */
export function classicCustomerAccountsSignUpReducer(
  action: AuthorizeModalTextAction,
  state: AuthorizeModalTextViewState,
): AuthorizeModalTextViewState {
  const newState = defaultPhoneAuthReducer(action, state);

  switch (newState.step) {
    case AuthorizeModalTextStep.Start:
    case AuthorizeModalTextStep.OneClick:
      return {
        ...newState,
        headerTemplate: 'customer_accounts.sign_up_page.auth_modal.login_title',
        descriptionTemplate:
          'customer_accounts.sign_up_page.auth_modal.login_description',
        legalTextVariant: 'generic',
        legalTextTemplate: 'login_with_shop.auth_modal.login_footer',
      };
    case AuthorizeModalTextStep.PhoneVerification:
      return {
        ...newState,
        headerTemplate: 'customer_accounts.sign_up_page.auth_modal.login_title',
        descriptionTemplate:
          'customer_accounts.sign_up_page.auth_modal.login_sms_description',
      };
    case AuthorizeModalTextStep.EmailVerification:
      return {
        ...newState,
        headerTemplate: 'customer_accounts.sign_up_page.auth_modal.login_title',
        descriptionTemplate:
          'customer_accounts.sign_up_page.auth_modal.login_email_description',
      };
  }

  return newState;
}
